import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success("Welcome — $100 credited to your balance");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-neo-bg text-white grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 border-r border-neo-border relative noise">
        <Link to="/" data-testid="auth-brand-link" className="font-mono uppercase tracking-[0.28em] text-sm">
          Neo.Invest
        </Link>
        <div>
          <h1 className="text-5xl font-black tracking-tighter uppercase leading-none">
            $100<br />
            <span className="bg-white text-black px-2">on the house.</span>
          </h1>
          <p className="mt-6 text-neutral-400 max-w-md">
            Every new account is funded with a $100 welcome credit. Trade any of 280+ assets at 1%.
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          // no card required · demo terminal
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">// open_account</div>
          <h2 className="mt-3 text-4xl font-black tracking-tighter uppercase">Open account</h2>
          <div className="mt-8 space-y-4">
            <Field label="Name">
              <input
                data-testid="register-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-transparent border border-neo-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-white transition-colors"
              />
            </Field>
            <Field label="Email">
              <input
                data-testid="register-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border border-neo-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-white transition-colors"
              />
            </Field>
            <Field label="Password (min 6)">
              <input
                data-testid="register-password-input"
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border border-neo-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-white transition-colors"
              />
            </Field>
          </div>
          <button
            data-testid="register-submit-button"
            type="submit"
            disabled={busy}
            className="mt-8 w-full bg-white text-black font-mono uppercase tracking-widest text-xs py-4 border border-white hover-invert disabled:opacity-60"
          >
            {busy ? "Opening…" : "Claim $100 bonus"}
          </button>

          <div className="mt-4 flex items-center gap-3 text-neutral-600 font-mono text-[10px] uppercase tracking-[0.3em]">
            <span className="h-px flex-1 bg-neo-border" /> or <span className="h-px flex-1 bg-neo-border" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton label="Sign up with Google" />
          </div>

          <div className="mt-6 text-xs text-neutral-500 font-mono">
            Already have an account?{" "}
            <Link data-testid="register-to-login-link" to="/login" className="text-white underline underline-offset-4">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
